import { borders } from './base/borders';
import {
  breakpoints,
  mediaQueries,
} from './base/breakpoints';
import { fontSizes } from './base/fontSizes';
import { letterSpacings } from './base/letterSpacings';
import { lineHeights } from './base/lineHeights';
import { radii } from './base/radii';
import { shadows } from './base/shadows';
import { space } from './base/space';
import { transitions } from './base/transitions';

/**
 * Dark-theme colors.
 *
 * MUST mirror the shape of the light theme (src/assets/themes/base/colors.ts)
 * so components referencing `theme.colors.gray[9]`, `theme.colors.primary[5]`,
 * etc. don't hit undefined lookups under dark mode. This is enforced by
 * src/assets/themes/__tests__/darkTheme.structure.test.ts.
 *
 * Conventions:
 *   gray / primary / error / success / warning are 10-entry arrays (0..9)
 *   where index 0 is the darkest/most-saturated and index 9 is the
 *   lightest/cleanest. This matches the light scales so that
 *   `theme.colors.gray[9]` always means "the background-most slot".
 *
 * Runtime CSS vars (used by Tailwind utilities) live in
 * src/assets/styles/tailwind.css under [data-theme="dark"]; those values
 * mirror this file.
 */
const colors = {
  transparent: 'transparent',
  white: '#ffffff',
  black: '#000000',

  gray: [
    '#1a1a1a', // 0 — main background
    '#2d2d2d', // 1 — surface
    '#404040', // 2 — muted surface / hover
    '#4d4d4d', // 3 — border
    '#666666', // 4 — emphasis border
    '#808080', // 5 — disabled text
    '#cccccc', // 6 — muted-foreground
    '#e6e6e6', // 7 — foreground
    '#f2f2f2', // 8 — emphasis foreground
    '#ffffff', // 9 — inverted background / strongest foreground
  ],

  primary: [
    '#1a365d', // 0
    '#2a4365', // 1
    '#2c5282', // 2
    '#2b6cb0', // 3
    '#4299e1', // 4 — main
    '#63b3ed', // 5
    '#90cdf4', // 6
    '#bee3f8', // 7
    '#ebf8ff', // 8
    '#f7fbff', // 9 — added for parity with light scale
  ],

  error: [
    '#3b1818', // 0
    '#5c2424', // 1
    '#7d2f2f', // 2
    '#9e3a3a', // 3
    '#e53e3e', // 4 — main
    '#f56565', // 5
    '#fc8181', // 6
    '#feb2b2', // 7
    '#fff5f5', // 8
    '#fff8f8', // 9 — added for parity
  ],

  success: [
    '#1c3829', // 0
    '#276749', // 1
    '#2f855a', // 2
    '#38a169', // 3
    '#48bb78', // 4 — main
    '#68d391', // 5
    '#9ae6b4', // 6
    '#c6f6d5', // 7
    '#f0fff4', // 8
    '#f7fffa', // 9 — added for parity
  ],

  warning: [
    // Dark-mode warning palette. Keep roughly in sync with light warning
    // by adjusting for dark-background contrast.
    '#3a2a0f', // 0
    '#4d3810', // 1
    '#624612', // 2
    '#7a5515', // 3
    '#b67626', // 4 — main
    '#d18a3b', // 5
    '#e5a25a', // 6
    '#f2bc85', // 7
    '#fce0b8', // 8
    '#fff3e0', // 9
  ],
} as const;

export const dark = {
  borders,
  breakpoints,
  colors,
  fontSizes,
  letterSpacings,
  lineHeights,
  mediaQueries,
  radii,
  shadows,
  space,
  transitions,
};
